import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import Logo from './Logo';
import DisclaimerModal from './DisclaimerModal';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import AppFooter from './AppFooter';

export default function AccessCodeModal({ onSuccess, onError }) {
  const [accessCode, setAccessCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [floatingElements, setFloatingElements] = useState([]);
  const submitInFlight = useRef(false);

  // Add resize event listener for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Generate floating elements
  useEffect(() => {
    const symbols = [
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
      'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
      '+', '-', '×', '÷', '=', '%', '@', '#', '&'
    ];
    
    const colors = [
      // Emerald family
      '#10b981', '#059669', '#047857', '#065f46',
      // Teal family  
      '#14b8a6', '#0d9488', '#0f766e', '#134e4a',
      // Green family
      '#22c55e', '#16a34a', '#15803d', '#166534',
      // Light emerald/mint
      '#34d399', '#6ee7b7', '#a7f3d0', '#d1fae5',
      // Cyan family
      '#06b6d4', '#0891b2', '#0e7490', '#155e75',
      // Blue-green
      '#20b2aa', '#008b8b', '#5f9ea0', '#4682b4',
      // Purple-blue accents
      '#8b5cf6', '#7c3aed', '#6d28d9', '#5b21b6',
      // Warm accents
      '#f59e0b', '#d97706', '#92400e', '#78350f'
    ];
    
    const pulseTypes = [
      { name: 'intense', duration: [1.5, 3], intensity: [0.8, 1] },
      { name: 'gentle', duration: [3, 6], intensity: [0.4, 0.7] },
      { name: 'rapid', duration: [0.8, 2], intensity: [0.9, 1] }
    ];
    
    const generateElements = () => {
      return Array.from({ length: 25 }, (_, i) => {
        const pulseType = pulseTypes[Math.floor(Math.random() * pulseTypes.length)];
        const pulseDuration = Math.random() * (pulseType.duration[1] - pulseType.duration[0]) + pulseType.duration[0];
        const baseOpacity = Math.random() * 0.3 + 0.1;
        const maxOpacity = Math.min(baseOpacity + (Math.random() * pulseType.intensity[1] * 0.6), 1);
        
        return {
          id: i,
          symbol: symbols[Math.floor(Math.random() * symbols.length)],
          x: Math.random() * 100,
          y: Math.random() * 100,
          size: Math.random() * 20 + 15,
          duration: Math.random() * 20 + 15,
          delay: Math.random() * 5,
          color: colors[Math.floor(Math.random() * colors.length)],
          rotation: Math.random() * 360,
          rotationSpeed: Math.random() * 4 + 1,
          moveX: (Math.random() - 0.5) * 200,
          moveY: (Math.random() - 0.5) * 200,
          // Pulsating properties
          pulseType: pulseType.name,
          pulseDuration: pulseDuration,
          pulseDelay: Math.random() * 3,
          minOpacity: baseOpacity,
          midOpacity: baseOpacity + (maxOpacity - baseOpacity) * 0.6,
          maxOpacity: maxOpacity,
          glowIntensity: Math.random() * 0.8 + 0.2,
        };
      });
    };

    setFloatingElements(generateElements());

    // Regenerate elements every 30 seconds
    const interval = setInterval(() => {
      setFloatingElements(generateElements());
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const isWithinAllowedTimeRange = (startTime, endTime) => {
    if (!startTime || !endTime) return true;
    const now = new Date();
    const currentHours = now.getHours();
    const currentMinutes = now.getMinutes();

    const [startHours, startMinutes] = startTime.split(':').map(Number);
    const [endHours, endMinutes] = endTime.split(':').map(Number);

    const currentTimeInMinutes = currentHours * 60 + currentMinutes;
    const startTimeInMinutes = startHours * 60 + startMinutes;
    const endTimeInMinutes = endHours * 60 + endMinutes;

    if (startTimeInMinutes > endTimeInMinutes) {
      return (
        currentTimeInMinutes >= startTimeInMinutes ||
        currentTimeInMinutes <= endTimeInMinutes
      );
    } else {
      return (
        currentTimeInMinutes >= startTimeInMinutes &&
        currentTimeInMinutes <= endTimeInMinutes
      );
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const normalizedAccessCode = accessCode.trim();
    if (submitInFlight.current || !normalizedAccessCode) return;
    submitInFlight.current = true;
    setError('');
    setIsLoading(true);

    try {
      const user = await api.verifyAccessCode(normalizedAccessCode);

      if (!user) {
        setError('Invalid access code. Please try again.');
        setAccessCode('');
        return;
      }

      // The server is the only authority for role resolution. In particular,
      // never ship or compare the administrator access code in browser code.
      if (user.role === 'admin') {
        onSuccess({
          ...user,
          accessCode: user.accessCode || normalizedAccessCode,
          isEmergencyAccess: false,
        });
        return;
      }

      const isSubUser = user.role === 'subuser';
      const isManager = user.role === 'teacher' || user.role === 'main_user';
      if (!isSubUser && !isManager) {
        setError('This access code is not authorized for this sign-in flow.');
        setAccessCode('');
        return;
      }
      if (user.isEnabled === false) {
        setError('This account is disabled. Please contact your administrator.');
        setAccessCode('');
        return;
      }

      const validityDays = parseInt(user.validityDays || 300, 10);
      const assignedDate = new Date(user.date_assigned || user.dateAssigned);
      const expiryDate = new Date(assignedDate);
      expiryDate.setDate(expiryDate.getDate() + validityDays);
      if (Number.isFinite(expiryDate.getTime()) && new Date() > expiryDate) {
        setError(
          'Your account has expired. Please contact your administrator.'
        );
        setAccessCode('');
        return;
      }

      onSuccess({
        ...user,
        role: user.role,
        accessCode: user.accessCode || normalizedAccessCode,
        requiresLogin: isManager,
        isEmergencyAccess: false,
      });
    } catch (err) {
      console.error('Error during access code verification:', err);
      setError(err?.code === 'AUTH_RATE_LIMITED'
        ? err.message
        : 'Failed to verify access code. Please try again.');
    } finally {
      submitInFlight.current = false;
      setIsLoading(false);
    }
  };

  // Add animation styles if they don't exist
  useEffect(() => {
    if (!document.querySelector('#enhanced-keyframes')) {
      const styleSheet = document.createElement('style');
      styleSheet.id = 'enhanced-keyframes';
      styleSheet.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse {
          0% { opacity: 0.6; }
          50% { opacity: 1; }
          100% { opacity: 0.6; }
        }
        @keyframes float {
          0% { transform: translateX(0px) translateY(0px) rotate(0deg); opacity: var(--min-opacity); }
          25% { opacity: var(--mid-opacity); }
          50% { transform: translateX(var(--move-x)) translateY(var(--move-y)) rotate(180deg); opacity: var(--max-opacity); }
          75% { opacity: var(--mid-opacity); }
          100% { transform: translateX(0px) translateY(0px) rotate(360deg); opacity: var(--min-opacity); }
        }
        @keyframes intensePulse {
          0%, 100% { opacity: var(--min-opacity); text-shadow: 0 0 5px var(--glow-color); }
          50% { opacity: var(--max-opacity); text-shadow: 0 0 20px var(--glow-color), 0 0 30px var(--glow-color); }
        }
        @keyframes gentlePulse {
          0%, 100% { opacity: var(--min-opacity); text-shadow: 0 0 3px var(--glow-color); }
          50% { opacity: var(--max-opacity); text-shadow: 0 0 8px var(--glow-color); }
        }
        @keyframes rapidPulse {
          0%, 100% { opacity: var(--min-opacity); text-shadow: 0 0 8px var(--glow-color); }
          50% { opacity: var(--max-opacity); text-shadow: 0 0 25px var(--glow-color), 0 0 35px var(--glow-color), 0 0 45px var(--glow-color); }
        }
        @keyframes shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        @keyframes gradientShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes blobPulse {
          0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.1; }
          50% { transform: scale(1.2) rotate(180deg); opacity: 0.15; }
        }
        .modal-button:hover {
          transform: scale(1.02);
          box-shadow: 0 6px 15px rgba(16, 185, 129, 0.4) !important;
        }
        .modal-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.5s;
        }
        .modal-button:hover::before {
          left: 100%;
        }
        .disclaimer-button:hover {
          background-color: rgba(153, 27, 27, 0.4) !important;
        }
        .logo-gradient {
          background: linear-gradient(45deg, #10b981, #14b8a6, #34d399, #6ee7b7);
          background-size: 200% 200%;
          animation: gradientShift 3s ease-in-out infinite;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          text-shadow: 0 0 20px rgba(16, 185, 129, 0.3);
        }
        ::placeholder {
          color: rgba(226, 232, 240, 0.5);
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Responsive styles
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(12px)',
    background: 'linear-gradient(to bottom, rgba(15, 23, 42, 0.95), rgba(30, 41, 59, 0.95), rgba(51, 65, 85, 0.95))',
  };

  // Enhanced decorative blobs
  const blobContainerStyle = {
    position: 'absolute',
    inset: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
    zIndex: -1,
  };

  const blob1Style = {
    position: 'absolute',
    top: '10%',
    left: '25%',
    width: '22rem',
    height: '22rem',
    background: 'radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 70%, transparent 100%)',
    borderRadius: '50%',
    filter: 'blur(70px)',
    animation: 'blobPulse 8s ease-in-out infinite',
  };

  const blob2Style = {
    position: 'absolute',
    bottom: '20%',
    right: '25%',
    width: '16rem',
    height: '16rem',
    background: 'radial-gradient(circle, rgba(13, 148, 136, 0.15) 0%, rgba(13, 148, 136, 0.05) 70%, transparent 100%)',
    borderRadius: '50%',
    filter: 'blur(70px)',
    animation: 'blobPulse 10s ease-in-out infinite 2s',
  };

  const blob3Style = {
    position: 'absolute',
    top: '30%',
    right: '30%',
    width: '12rem',
    height: '12rem',
    background: 'radial-gradient(circle, rgba(139, 92, 246, 0.15) 0%, rgba(139, 92, 246, 0.05) 70%, transparent 100%)',
    borderRadius: '50%',
    filter: 'blur(70px)',
    animation: 'blobPulse 12s ease-in-out infinite 4s',
  };

  const modalStyle = {
    background: 'rgba(15, 23, 42, 0.6)',
    padding: isMobileView ? '1.5rem' : '2.5rem',
    borderRadius: '1.5rem',
    width: '100%',
    maxWidth: isMobileView ? '90%' : '500px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
    animation: 'fadeIn 0.4s ease',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    backdropFilter: 'blur(16px)',
    position: 'relative',
    overflow: 'hidden',
    color: 'white',
  };

  const brandingContainerStyle = {
    marginBottom: '1.5rem',
    textAlign: 'center',
    borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
    paddingBottom: '1rem',
  };

  const brandingTitleStyle = {
    fontSize: isMobileView ? '2rem' : '2.5rem',
    fontWeight: 'bold',
    margin: 0,
    letterSpacing: '0.03em',
    cursor: 'pointer',
  };

  const brandingSubtitleStyle = {
    fontSize: '0.9rem',
    margin: 0,
  };

  const brandingTaglineStyle = {
    fontSize: '1rem',
    fontStyle: 'italic',
    marginTop: '0.3rem',
  };

  const titleStyle = {
    fontSize: isMobileView ? '1.2rem' : '1.4rem',
    fontWeight: 'bold',
    marginBottom: '1.2rem',
    textAlign: 'center',
    color: 'white',
    letterSpacing: '0.02em',
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  };

  const inputContainerStyle = {
    position: 'relative',
    marginBottom: '0.5rem',
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem',
    fontSize: '1.125rem',
    border: '1px solid rgba(148, 163, 184, 0.3)',
    borderRadius: '0.75rem',
    textAlign: 'center',
    letterSpacing: '0.15em',
    boxSizing: 'border-box',
    outline: 'none',
    transition: 'all 0.3s ease',
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    backgroundColor: 'rgba(30, 41, 59, 0.5)',
    backdropFilter: 'blur(10px)',
    color: 'white',
  };

  const buttonStyle = {
    width: '100%',
    padding: '0.875rem',
    background: isLoading ? '#0d9488' : 'linear-gradient(45deg, #10b981, #14b8a6)',
    backgroundSize: '200% 200%',
    color: 'white',
    border: 'none',
    borderRadius: '0.75rem',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 10px rgba(16, 185, 129, 0.3)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    margin: '0.75rem 0',
    position: 'relative',
    overflow: 'hidden',
    animation: isLoading ? 'none' : 'gradientShift 3s ease infinite',
  };

  const errorStyle = {
    color: '#fecaca',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    backgroundColor: 'rgba(153, 27, 27, 0.3)',
    padding: '0.75rem 1rem',
    borderRadius: '0.625rem',
    border: '1px solid #b91c1c',
    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.1)',
    fontSize: '0.875rem',
    fontWeight: '500',
    width: '100%',
    boxSizing: 'border-box',
  };

  const spinnerStyle = {
    animation: 'spin 1.2s linear infinite',
    width: '1.25rem',
    height: '1.25rem',
    border: '3px solid rgba(255, 255, 255, 0.2)',
    borderTopColor: 'white',
    borderRadius: '50%',
    display: 'inline-block',
  };

  const footerTextStyle = {
    fontSize: '0.75rem',
    textAlign: 'center',
    marginTop: '1.5rem',
    color: '#94a3b8',
    lineHeight: '1.4',
  };

  const disclaimerLinkStyle = {
    backgroundColor: 'rgba(153, 27, 27, 0.2)',
    color: '#fecaca',
    fontWeight: 'bold',
    padding: '0.75rem 1rem',
    borderRadius: '0.75rem',
    textAlign: 'center',
    marginTop: '1.5rem',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    boxShadow: '0 2px 6px rgba(211, 47, 47, 0.15)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
    border: '1px solid rgba(220, 38, 38, 0.3)',
  };

  const brandingLinkStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    fontSize: '0.875rem',
    marginTop: '0.5rem',
    textDecoration: 'none',
    transition: 'all 0.2s ease',
  };

  const floatingElementStyle = (element) => ({
    position: 'absolute',
    left: `${element.x}%`,
    top: `${element.y}%`,
    fontSize: `${element.size}px`,
    color: element.color,
    fontWeight: 'bold',
    pointerEvents: 'none',
    userSelect: 'none',
    animation: `
      float ${element.duration}s ease-in-out infinite ${element.delay}s,
      ${element.pulseType}Pulse ${element.pulseDuration}s ease-in-out infinite ${element.pulseDelay}s
    `,
    '--move-x': `${element.moveX}px`,
    '--move-y': `${element.moveY}px`,
    '--min-opacity': element.minOpacity,
    '--mid-opacity': element.midOpacity,
    '--max-opacity': element.maxOpacity,
    '--glow-color': `${element.color}${Math.floor(element.glowIntensity * 255).toString(16).padStart(2, '0')}`,
    zIndex: 0,
  });

  return (
    <div style={overlayStyle}>
      {/* Enhanced decorative blobs */}
      <div style={blobContainerStyle}>
        <div style={blob1Style}></div>
        <div style={blob2Style}></div>
        <div style={blob3Style}></div>
      </div>

      {/* Floating Elements */}
      {floatingElements.map((element) => (
        <div
          key={element.id}
          style={floatingElementStyle(element)}
        >
          {element.symbol}
        </div>
      ))}
      
      <div style={modalStyle}>
        <div style={brandingContainerStyle}>
          <Logo />
          <div style={{ marginTop: '0.75rem' }}>
            <a 
              href="https://assessnow.xyz" 
              target="_blank" 
              rel="noopener noreferrer" 
              style={{ textDecoration: 'none' }}
            >
              <h1 style={brandingTitleStyle}>
                <span className="logo-gradient" style={{ color: '#10b981' }}>assess</span>
                <span className="logo-gradient" style={{ color: 'white' }}>now</span>
                <span className="logo-gradient" style={{ color: '#14b8a6', fontSize: '60%', verticalAlign: 'super' }}>.xyz</span>
              </h1>
            </a>
            <p style={{...brandingSubtitleStyle, color: '#94a3b8'}}>A CBA Initiative & Development</p>
            <p style={{...brandingTaglineStyle, color: '#e2e8f0'}}>
              "Knowledge Measured, Growth Delivered"
            </p>
            <a 
              href="https://assessnow.xyz" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{...brandingLinkStyle, color: '#34d399'}}
            >
              Visit our website <ExternalLink size={14} />
            </a>
          </div>
        </div>

        <h2 style={titleStyle}>Enter Access Code</h2>

        <div style={formStyle}>
          <div style={inputContainerStyle}>
            <input
              type="password"
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleSubmit(event);
              }}
              placeholder="Enter your access code"
              style={inputStyle}
              disabled={isLoading}
              autoFocus
              maxLength="128"
            />
          </div>

          {error && (
            <div style={errorStyle}>
              <AlertTriangle size={16} color="#dc2626" />
              <span>{error}</span>
            </div>
          )}

          <button
            type="button"
            style={buttonStyle}
            disabled={isLoading || !accessCode.trim()}
            className="modal-button"
            onClick={handleSubmit}
          >
            {isLoading ? (
              <>
                <span style={spinnerStyle}></span>
                Verifying...
              </>
            ) : (
              'Submit'
            )}
          </button>

          {/* Disclaimer Link */}
          <div
            style={disclaimerLinkStyle}
            onClick={() => setShowDisclaimer(true)}
            className="disclaimer-button"
          >
            <AlertTriangle size={16} />
            Read Disclaimer First Before Use
          </div>

          <div style={footerTextStyle}>
            <AppFooter compact style={{ padding: 0, color: 'inherit' }} />
            <p style={{ margin: '0.25rem 0' }}>For Inquiry, email: cromarts@myyahoo.com</p>
          </div>
        </div>
      </div>

      {showDisclaimer && (
        <DisclaimerModal onClose={() => setShowDisclaimer(false)} />
      )}
    </div>
  );
}
